import type { PropertyValues, TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { PageContent } from 'src/pages/page-content.js';
import { leavePeriodStyles } from 'src/library/leave-period-styles';
import 'src/library/lists/d-list-section';
import 'src/library/elements/d-action';
import 'src/library/elements/d-progress-bar';
import 'src/library/elements/d-bar-graph';
import { capitalize } from 'src/utilities/text';
import { DPromoDialog } from 'src/library/promo/d-promo-dialog';
import { getEven } from 'src/utilities/layout';
import { dateTimeDisplayShort } from 'src/store/utilities';
import _ from 'lodash';
import { features } from '../../store/selectors/features/features';
import { promo } from 'src/store/selectors/promo';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import type { NameValue } from '../../utilities/nameValue';
import type { FormItemForStatusPage, StatusPageViewModel } from './status-page-view-model';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { PromoDialogResult } from '../../library/promo/promo-dialog-result';
import type { FormDialogResult } from 'src/library/components/form-dialog-result';
import { FormDialog } from 'src/library/components/form-dialog';
import { assertIsDefined } from '../../assertIsDefined';
import { filterFormStructure } from './filter-form-structure';
import type { UpdateFormValuesDetail } from './update-form-values-detail';
import type { EditPeriodsInput } from 'src/pages/edit-periods-input';
import type { EditPeriodsResult } from 'src/pages/edit-periods-result';
import { EditPeriodsDialog } from 'src/pages/edit-periods-dialog';
import type { EditPeriodResult } from 'src/pages/edit-period-result';
import type { EmployeeForStaffingCalendar } from 'src/store/selectors';
import type { ActionInput } from 'src/library/elements/action-input';
import 'src/library/elements/d-more-menu';
import { HiddenWidgetsService } from './hidden-widgets-service';
import { WidgetOrderService } from './widget-order-service';
import { exportStatusPageToExcel } from 'src/services/excel-export/status-page-excel-export';

interface Widget {
  name: string;
  active: boolean;
  promo: string;
}

/**
 *
 * STATUS OK
 */
@customElement('d-status-page-content')
export class DStatusPageContent extends PageContent<StatusPageViewModel> {
  static readonly styles = [
    ...super.styles,
    leavePeriodStyles,
    css`
      :host {
        display: block;
        direction: ltr;
        color: var(--text-color);
      }
      #measure {
        position: fixed;
        top: -10000000px;
        opacity: 0;
      }
      .wrapper {
        display: flex;
        gap: 20px;
        margin-top: 20px;
      }
      .column {
        flex: 1;
        max-width: 500px;
      }
      .widget {
        box-shadow: 0 0 10px hsla(1, 0%, 0%, 0.2);
        margin-bottom: 20px;
        background: white;
        border-radius: 12px;
        padding: 18px 20px 12px;
        line-height: 130%;
      }
      .widget.promo {
        box-shadow: none;
        opacity: 0.7;
        cursor: pointer;
      }
      @media (hover: hover) {
        .widget.promo:hover {
          box-shadow: 0 0 10px hsla(1, 0%, 0%, 0.2);
          opacity: 1;
          transform: scale(1.01);
          transition: all 0.3s;
        }
      }
      .name {
        font-size: 22px;
        line-height: 120%;
        font-weight: 500;
        margin-bottom: 6px;
      }
      .name:last-child {
        margin-bottom: 8px;
      }
      .subtitle {
        font-size: 17px;
        line-height: 120%;
        font-weight: 500;
        margin-top: 8px;
        margin-bottom: 6px;
      }
      .status-text {
        font-weight: 400;
        padding: 5px 0;
      }
      .status-text.info {
        background: url(/images/info-fill.svg) -2px 3px no-repeat;
        background-size: 24px auto;
        padding-left: 26px;
      }
      .status-text.checked {
        background: url(/images/check-mini-blue.svg) -2px 3px no-repeat;
        background-size: 24px auto;
        padding-left: 26px;
      }
      .status-text.alert {
        background: url(/images/alert.svg) -2px 2px no-repeat;
        background-size: 24px auto;
        padding-left: 26px;
      }
      d-action {
        margin-left: -8px;
      }
      d-progress-bar {
        height: 8px;
        margin: 10px 0 6px 0;
      }
      table.risk {
        width: 100%;
        border: none;
      }
      table.risk td {
        width: 90%;
        padding: 2px 0;
        vertical-align: top;
      }
      table.risk td:first-child {
        width: 10%;
        padding-right: 10px;
        font-size: 15px;
        font-family: var(--mainSans), sans-serif;
        color: var(--text-color);
        white-space: nowrap;
      }
      d-bar-graph {
        width: 100%;
        margin-top: 7px;
        margin-bottom: -7px;
      }
      .kpi-value {
        font-size: 40px;
        line-height: 110%;
        font-weight: 600;
        margin: 4px 0 2px;
        color: var(--text-color);
      }
      .kpi-unit {
        font-size: 20px;
        font-weight: 500;
      }
      .kpi-sub {
        font-weight: 400;
        padding: 2px 0 5px;
        color: var(--text-color);
      }
      .widget-wrapper {
        position: relative;
      }
      .widget-wrapper.dragging {
        opacity: 0.5;
      }
      .widget-wrapper.drag-over {
        border-top: 3px solid var(--themeColor, #4caf50);
      }
      .widget-wrapper .drag-handle {
        position: absolute;
        left: -5px;
        top: 18px;
        cursor: grab;
        opacity: 0;
        transition: opacity 0.2s;
        padding: 4px;
        color: #999;
        font-size: 18px;
        user-select: none;
        z-index: 11;
      }
      .widget-wrapper .drag-handle:active {
        cursor: grabbing;
      }
      .widget-wrapper:hover .drag-handle {
        opacity: 1;
      }
      .widget-hidden {
        opacity: 0.4;
        transition: opacity 0.3s;
      }
      .widget-hidden:hover {
        opacity: 0.6;
      }
      .widget-visibility-controls {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.2s;
      }
      .widget-wrapper:hover .widget-visibility-controls {
        opacity: 1;
      }
      .widget-visibility-controls button {
        padding: 6px 12px;
        background: white;
        border: 1px solid var(--borderColorLight, #ddd);
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        color: var(--text-color);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      .widget-visibility-controls button:hover {
        background: var(--backgroundLightGray, #f5f5f5);
      }
    `,
  ];

  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  showUpdates = false;
  @property({ type: Boolean })
  showStartTasks = false;
  @property({ type: Boolean })
  showAllIssues = false;
  @property({ type: Array })
  columns: Widget[][] = [];
  @state()
  columnsCount = 1;
  @state()
  private debouncedSetColumns = _.debounce(() => this.setColumns(), 100, { maxWait: 100 });
  @state()
  editStatusPage = false;
  @state()
  private hiddenWidgets: string[] = [];
  @state()
  private widgetOrder: string[] = [];
  @state()
  private draggedWidgetId: string | null = null;
  @state()
  private dragOverWidgetId: string | null = null;

  protected get currentItemSublabel() {
    return capitalize(this.pageView.today.toStringForDisplayWithDayOfWeekAndYear());
  }

  private get widgetHeights(): NameValue[] {
    const wrapper = this.shadowRoot?.getElementById('wrapper');
    if (wrapper) {
      const gap = 20;
      const columnWidth = (wrapper.offsetWidth - gap * (this.columnsCount - 1)) / this.columnsCount;
      const measure = this.shadowRoot?.getElementById('measure');
      if (measure) {
        measure.style.width = columnWidth + 'px';
        const widgets = [...measure.getElementsByClassName('widget')] as HTMLElement[];
        return widgets.map((w: HTMLElement): NameValue => {
          return {
            name: w.dataset.name ?? '',
            value: w.offsetHeight,
          };
        });
      }
    }
    return [];
  }

  private get widgets(): Widget[] {
    const m = this.featureStates;
    // TEMPORARY: Handle PERFORMANCE_REVIEW accounts specially
    // For PERFORMANCE_REVIEW-only accounts, only show the interviews widget
    // TODO: Remove this override once the server properly handles PERFORMANCE_REVIEW

    // Check if this is a PERFORMANCE_REVIEW-only account (no other features enabled)
    const isPerformanceReviewOnly =
      m.performanceReview &&
      !m.core &&
      !m.hse &&
      !m.meetings &&
      !m.staffing &&
      !m.issues &&
      !m.risk &&
      !m.healthcare &&
      !m.norengros &&
      !m.assets &&
      !m.substances &&
      !m.data &&
      !m.quality &&
      !m.collections;

    // For PERFORMANCE_REVIEW-only accounts (including read-only), only return the interviews widget
    if (isPerformanceReviewOnly) {
      return [
        {
          name: 'interviews',
          active: true,
          promo: '',
        },
      ];
    }

    // Normal logic for other accounts
    const hasMeetingsOrPerformanceReview = m.meetings || m.performanceReview;

    const activeWidgets: Widget[] = [];
    const inactiveWidgets: Widget[] = [];
    const widgets: Widget[] = [
      {
        name: 'myThings',
        active: m.core || m.hse || hasMeetingsOrPerformanceReview,
        promo: '',
      },
      // { name: 'forms', active: m.core || m.hse, promo: '' },
      { name: 'staffing', active: m.staffing, promo: 'staffing' },
      { name: 'overdue', active: m.core || hasMeetingsOrPerformanceReview, promo: '' },
      { name: 'drafts', active: m.core || hasMeetingsOrPerformanceReview, promo: '' },
      { name: 'updates', active: m.core || hasMeetingsOrPerformanceReview, promo: '' },
      { name: 'issues', active: m.issues, promo: 'issues' },
      { name: 'startTasks', active: m.core || hasMeetingsOrPerformanceReview, promo: '' },
      { name: 'meetings', active: hasMeetingsOrPerformanceReview, promo: 'meetings' },
      { name: 'interviews', active: hasMeetingsOrPerformanceReview, promo: 'meetings' },
      { name: 'functions', active: m.core || hasMeetingsOrPerformanceReview, promo: '' },
      { name: 'risk', active: m.risk, promo: 'risk' },
      { name: 'healthcare', active: m.healthcare, promo: 'healthcare' },
      { name: 'norengros', active: m.norengros, promo: this.pageView.sector === 'HEALTH' ? 'norengros' : '' },
    ];

    // Ledelsesdashbord: legg HMS-KPI-widgets først (issue #13276, krav 1.7.2)
    if (m.managementDashboard && this.pageView.kpis) {
      widgets.unshift(
        { name: 'kpiIssues', active: true, promo: '' },
        { name: 'kpiClosingTime', active: true, promo: '' },
        { name: 'kpiSickLeave', active: true, promo: '' },
      );
    }

    widgets.forEach((widget) => {
      // I redigeringsmodus vises alle widgets, ellers skjules de skjulte
      const isHidden = this.hiddenWidgets.includes(widget.name);
      if (this.editStatusPage || !isHidden) {
        if (widget.active) {
          activeWidgets.push(widget);
        } else if (widget.promo) {
          inactiveWidgets.push(widget);
        }
      }
    });

    const allWidgets = [...activeWidgets, ...inactiveWidgets];

    // Bruk egendefinert rekkefølge hvis brukeren har omorganisert widgetene
    if (this.widgetOrder.length > 0) {
      allWidgets.sort((a, b) => {
        const indexA = this.widgetOrder.indexOf(a.name);
        const indexB = this.widgetOrder.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
    }

    return allWidgets;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadHiddenWidgets();
    this.loadWidgetOrder();
  }

  onEditStatusPage() {
    this.editStatusPage = !this.editStatusPage;
  }

  onResize(_contentRect: DOMRectReadOnly) {
    super.onResize(_contentRect);
    this.debouncedSetColumns();
  }

  async onPromoClick(feature: string) {
    const promoData = promo.find((p) => {
      return p.id === feature;
    });
    if (promoData) {
      const result: PromoDialogResult = await DPromoDialog.open({
        promoData,
        features: features,
        employeesCount: this.pageView.employeesCount,
        specialTerms: this.pageView.specialTerms,
        healthcareSignatoryOptions: this.pageView.healthcareSignatoryOptions,
        healthcareSignatory: this.pageView.currentEmployeeUuid,
        canUpgrade: this.writeAccess,
        hasHealthcare: this.featureStates.healthcare,
      });
      if (result.action === 'upgrade' && promoData.pageAfterUpgrade)
        this.dispatchEvent(
          new CustomEvent('navigate', {
            bubbles: true,
            composed: true,
            detail: { href: '/account/' + this.pageView.organizationId + '/' + promoData.pageAfterUpgrade },
          }),
        );
    }
  }

  renderMyThings(widget: Widget) {
    if (widget.active && this.pageView.currentEmployeeInfo) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.myThings')}</div>
          ${this.pageView.currentEmployeeInfo.map((item) => {
            if (item.href) {
              return html`<d-action plain href="${item.href}">${item.text}</d-action>`;
            }
            return html`<div class="status-text ${item.class}">${item.text}</div>`;
          })}
        </div>
      `;
    }
    return html``;
  }

  /* TODO
  renderForms(widget: Widget) {
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.myThings')}</div>
          ${this.pageView.forms.map((item) => {
            if (item.href) {
              return html`<d-action plain href="${item.href}">${item.text}</d-action>`;
            }
            return html`<div class="status-text ${item.class}">${item.text}</div>`;
          })}
        </div>
      `;
    }
    return html``;
  }
   */

  renderStaffing(widget: Widget) {
    let unconfirmedLeavePeriods: ListSectionItemInput[] | undefined = undefined;
    if (this.pageView.unconfirmedLeavePeriods) {
      unconfirmedLeavePeriods = this.pageView.unconfirmedLeavePeriods.map((p) => ({
        ...p,
        clickHandler: () => this.onOpenPeriodDialog(p.clickData ?? ''),
      }));
    }
    if (this.pageView.leavePeriodsToday) {
      if (widget.active) {
        return html`
          <div class="widget" data-name="${widget.name}">
            <div class="name">${t('status.staffingAndAbsence')}</div>
            ${this.pageView.leavePeriodsToday.length
              ? html` <d-list-section no-header .items=${this.pageView.leavePeriodsToday}></d-list-section> `
              : html` <div class="status-text checked">${t('status.noAbsenceToday')}</div> `}
            ${unconfirmedLeavePeriods
              ? html`
                  <div class="subtitle">${t('status.unconfirmedAbsence')}</div>
                  <d-list-section
                    no-header
                    allow-show-limited
                    .items=${unconfirmedLeavePeriods}
                    .limitItems=${3}
                  ></d-list-section>
                `
              : nothing}
            <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.staffing}"
              >${t('status.goToStaffingCalendar')}
            </d-action>
          </div>
        `;
      }
      if (widget.promo) {
        return html`
          <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
            <div class="name">${t('status.staffingAndAbsence')}</div>
          </div>
        `;
      }
    }
    return html``;
  }

  renderOverdue(widget: Widget) {
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.overdueTasks')}</div>
          ${this.pageView.overdueEvents.length
            ? html` <d-list-section no-header .items=${this.pageView.overdueEvents}></d-list-section> `
            : html` <div class="status-text checked">${t('status.noOverdueTasks')}</div> `}
          <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.events}"
            >${t('status.goToWeeklyPlan')}</d-action
          >
        </div>
      `;
    }
    return html``;
  }

  renderDrafts(widget: Widget) {
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.drafts')}</div>
          ${this.pageView.drafts.length
            ? html` <d-list-section no-header .items=${this.pageView.drafts}></d-list-section> `
            : html`<div class="status-text checked">${t('status.noDrafts')}</div> `}
        </div>
      `;
    }
    return html``;
  }

  renderIssues(widget: Widget, interactive: boolean) {
    if (widget.active) {
      const toggleShowLabel = this.showAllIssues ? t('status.showFewer') : t('status.showAll');
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.activeIssues')}</div>
          ${this.pageView.issues.length
            ? html`
                <d-list-section
                  no-header
                  .items=${this.pageView.issues.filter((_item, index) => {
                    return index < 3;
                  })}
                ></d-list-section>
                <d-expansion .opened=${this.showAllIssues && interactive}>
                  <d-list-section
                    no-header
                    .items=${this.pageView.issues.filter((_item, index) => {
                      return index > 2;
                    })}
                  ></d-list-section>
                </d-expansion>
              `
            : html` <div class="status-text checked">${t('status.noOpenIssues')}</div> `}
          ${this.pageView.issues.length > 3
            ? html`<d-action plain @click=${() => (this.showAllIssues = !this.showAllIssues)}
                >${toggleShowLabel}</d-action
              >`
            : nothing}
          <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.issues}"
            >${t('status.goToIssues')}
          </d-action>
        </div>
      `;
    }
    if (widget.promo) {
      return html`
        <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
          <div class="name">${t('status.issues')}</div>
        </div>
      `;
    }
    return html``;
  }

  renderUpdates(widget: Widget, interactive: boolean) {
    if (widget.active) {
      const toggleShowLabel = this.showUpdates ? t('status.hideUpdates') : t('status.showUpdates');
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.updates')}</div>
          <d-list-section no-header .items=${this.pageView.documentUpdates.summary}></d-list-section>
          ${this.pageView.documentUpdates.items.length > 0
            ? html`
                <d-action plain @click=${() => (this.showUpdates = !this.showUpdates)}>${toggleShowLabel}</d-action>
              `
            : nothing}
          <d-expansion .opened=${this.showUpdates && interactive}>
            <d-list-section
              style="margin-top: 6px"
              no-header
              .items=${this.pageView.documentUpdates.items}
            ></d-list-section>
          </d-expansion>
        </div>
      `;
    }
    return html``;
  }

  renderStartTasks(widget: Widget, interactive: boolean) {
    if (widget.active) {
      const toggleShowLabel = this.showStartTasks ? t('status.hideRemaining') : t('status.showRemaining');
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.startTasks')}</div>
          ${this.pageView.startTasks.items.length
            ? html`
                <d-progress-bar
                  .total=${this.pageView.startTasks.total}
                  .partNumber=${this.pageView.startTasks.done}
                ></d-progress-bar>
              `
            : nothing}
          <div class="status-text ${this.pageView.startTasks.items.length === 0 ? 'checked' : ''}">
            ${this.pageView.startTasks.statusText}
          </div>
          ${this.pageView.startTasks.items.length
            ? html`
                <d-action plain @click=${() => (this.showStartTasks = !this.showStartTasks)}
                  >${toggleShowLabel}
                </d-action>
                <d-expansion ?opened=${this.showStartTasks && interactive}>
                  <d-list-section
                    .items=${this.pageView.startTasks.items}
                    no-header
                    start-tasks
                    @item-clicked=${this.onStartTaskClicked}
                  ></d-list-section>
                </d-expansion>
              `
            : nothing}
        </div>
      `;
    }
    if (widget.promo) {
      return html`
        <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
          <div class="name">${t('status.startTasks')}</div>
        </div>
      `;
    }
    return html``;
  }

  renderMeetings(widget: Widget) {
    if (this.pageView.futureMeetings) {
      if (widget.active) {
        return html`
          <div class="widget" data-name="${widget.name}">
            <div class="name">${t('status.meetings')}</div>
            ${this.pageView.futureMeetings.length
              ? html` <d-list-section no-header .items=${this.pageView.futureMeetings}></d-list-section> `
              : html` <div class="status-text checked">${t('status.noMeetingsTodayOrTomorrow')}</div> `}
            <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.meetings}">
              ${t('status.goToMeetings')}
            </d-action>
          </div>
        `;
      }
      if (widget.promo) {
        return html`
          <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
            <div class="name">${t('status.meetingsAndInterviews')}</div>
          </div>
        `;
      }
    }
    return html``;
  }

  renderInterviews(widget: Widget) {
    if (widget.active && this.pageView.interviewInfo) {
      // Check if this is a performance review-only account with write access (leader)
      const isPerformanceReviewOnly =
        this.featureStates.performanceReview &&
        !this.featureStates.core &&
        !this.featureStates.hse &&
        !this.featureStates.meetings &&
        !this.featureStates.staffing &&
        !this.featureStates.issues &&
        !this.featureStates.risk &&
        !this.featureStates.healthcare &&
        !this.featureStates.norengros &&
        !this.featureStates.assets &&
        !this.featureStates.substances &&
        !this.featureStates.data &&
        !this.featureStates.quality &&
        !this.featureStates.collections;

      // Leader widget for performance review-only accounts with write access
      if (isPerformanceReviewOnly && this.writeAccess && this.pageView.leaderInterviewInfo) {
        return html`
          <div class="widget" data-name="${widget.name}">
            <div class="name">${t('status.interviews')}</div>

            ${
              /* Meetings where current user is participant */
              this.pageView.leaderInterviewInfo.withCurrentUser &&
              this.pageView.leaderInterviewInfo.withCurrentUser.length > 0
                ? html`
                    <div class="subtitle">${t('status.myMeetings')}</div>
                    ${this.pageView.leaderInterviewInfo.withCurrentUser.map((item) => {
                      return html`
                        <div class="status-text ${item.status}">
                          ${item.employeeName}:
                          <d-action
                            plain
                            href="${'/account/' +
                            this.pageView.organizationId +
                            '/' +
                            pageIds.meetings +
                            '/meetingOccurrences/' +
                            item.meetingUuid}"
                          >
                            ${dateTimeDisplayShort(item.date, item.time)}
                          </d-action>
                        </div>
                      `;
                    })}
                  `
                : nothing
            }
            ${
              /* Meetings where current user is NOT participant */
              this.pageView.leaderInterviewInfo.withoutCurrentUser &&
              this.pageView.leaderInterviewInfo.withoutCurrentUser.length > 0
                ? html`
                    <div class="subtitle">${t('status.otherScheduledInterviews')}</div>
                    <div class="status-text checked">
                      ${t('status.namesHaveInterviews', {
                        names: this.pageView.leaderInterviewInfo.withoutCurrentUser
                          .map((e) => e.employeeName)
                          .join(', '),
                      })}
                    </div>
                  `
                : nothing
            }
            ${
              /* Employees without planned reviews */
              this.pageView.leaderInterviewInfo.notPlanned && this.pageView.leaderInterviewInfo.notPlanned.length > 0
                ? html`
                    <div class="subtitle">${t('status.notPlanned')}</div>
                    <div class="status-text alert">
                      ${t('status.namesHaveNoPlannedInterview', {
                        names: this.pageView.leaderInterviewInfo.notPlanned.map((e) => e.employeeName).join(', '),
                      })}
                    </div>
                  `
                : nothing
            }

            <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.meetings}">
              ${t('status.goToInterviews')}
            </d-action>
          </div>
        `;
      }

      // Custom widget for read-only users
      if (!this.writeAccess) {
        return html`
          <div class="widget" data-name="${widget.name}">
            <div class="name">${t('status.interviews')}</div>
            ${this.pageView.interviewInfo.length > 0
              ? this.pageView.interviewInfo.map((item) => {
                  if (item.date && item.meetingUuid) {
                    // Structure similar to healthcare widget - label text and clickable date with time
                    return html`
                      <div class="status-text ${item.status}">
                        ${item.text}
                        <d-action
                          plain
                          href="${'/account/' +
                          this.pageView.organizationId +
                          '/' +
                          pageIds.meetings +
                          '/meetingOccurrences/' +
                          item.meetingUuid}"
                        >
                          ${dateTimeDisplayShort(item.date, item.time)}
                        </d-action>
                      </div>
                    `;
                  } else {
                    // Just show the text without link
                    return html`<div class="status-text ${item.status}">${item.text}</div>`;
                  }
                })
              : html`<div class="status-text info">${t('status.noInterviewInfo')}</div>`}
            <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.meetings}">
              ${t('status.seeAllInterviews')}
            </d-action>
          </div>
        `;
      }

      // Standard widget for users with write access
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.interviews')}</div>
          ${this.pageView.interviewInfo.map((item) => {
            return html`<div class="status-text ${item.status}">${item.text}</div>`;
          })}
          <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.meetings}">
            ${t('status.goToMeetings')}
          </d-action>
        </div>
      `;
    }
    return html``;
  }

  renderFunctions(widget: Widget) {
    if (widget.active && this.pageView.functionsInfo) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.functions')}</div>
          <d-list-section style="margin-top: 6px" no-header .items=${this.pageView.functionsInfo}></d-list-section>
        </div>
      `;
    }
    return html``;
  }

  renderHealthcare(widget: Widget) {
    const items: ListSectionItemInput[] = this.pageView.healthcareStatus;
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.integratedHealthcare')}</div>
          <d-list-section style="margin-top: 6px" no-header .items=${items}></d-list-section>
        </div>
      `;
    }
    if (widget.promo) {
      return html`
        <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
          <div class="name">${t('status.integratedHealthcare')}</div>
        </div>
      `;
    }
    return html``;
  }
  renderNorengros(widget: Widget) {
    const items: ListSectionItemInput[] = this.pageView.norengrosStatus;
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">NorEngros</div>
          <d-list-section style="margin-top: 6px" no-header .items=${items}></d-list-section>
          <div class="actions">
            <d-action plain href="https://norengros.no" blank> ${t('status.goToWebShop')} </d-action>
          </div>
        </div>
      `;
    }
    if (widget.promo) {
      return html`
        <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
          <div class="name">${t('status.norengrosSpecialDiscount')}</div>
        </div>
      `;
    }
    return html``;
  }

  renderRisk(widget: Widget) {
    if (widget.active) {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.risk')}</div>
          <table class="risk">
            ${this.pageView.riskGroups.map((r) => {
              return html`
                <tr>
                  <td>${r.label}</td>
                  <td>
                    <d-bar-graph .items=${r.items}></d-bar-graph>
                  </td>
                </tr>
              `;
            })}
          </table>
          <d-action plain href="${'/account/' + this.pageView.organizationId + '/' + pageIds.riskAssessments}"
            >${t('status.goToRiskAssessments')}
          </d-action>
        </div>
      `;
    }
    if (widget.promo) {
      return html`
        <div class="widget promo" data-name="${widget.name}" @click=${() => this.onPromoClick(widget.promo)}>
          <div class="name">${t('status.risk')}</div>
        </div>
      `;
    }
    return html``;
  }

  renderKpiWidget(widget: Widget) {
    const kpis = this.pageView.kpis;
    if (!kpis) return html``;
    const orgId = this.pageView.organizationId;
    if (widget.name === 'kpiIssues') {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.openIssues')}</div>
          <div class="kpi-value">${kpis.openIssues}</div>
          <div class="kpi-sub">${t('status.newLast12Months', { count: kpis.newIssuesLast12Months })}</div>
          <d-action plain href="${'/account/' + orgId + '/' + pageIds.issues}">${t('status.goToIssues')}</d-action>
        </div>
      `;
    }
    if (widget.name === 'kpiClosingTime') {
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.closingTime')}</div>
          ${kpis.averageClosingDays !== null
            ? html`<div class="kpi-value">
                ${kpis.averageClosingDays}<span class="kpi-unit"> ${t('status.days')}</span>
              </div>`
            : html`<div class="kpi-value">–</div>`}
          <div class="kpi-sub">${t('status.closingTimeSubtitle', { count: kpis.closedIssuesLast12Months })}</div>
          <d-action plain href="${'/account/' + orgId + '/' + pageIds.issues}">${t('status.goToIssues')}</d-action>
        </div>
      `;
    }
    if (widget.name === 'kpiSickLeave') {
      const hasData = kpis.sickLeavePercent !== null;
      return html`
        <div class="widget" data-name="${widget.name}">
          <div class="name">${t('status.sickLeave')}${kpis.sickLeaveYearName ? ' ' + kpis.sickLeaveYearName : ''}</div>
          ${hasData
            ? html`
                <div class="kpi-value">${kpis.sickLeavePercent}<span class="kpi-unit"> %</span></div>
                <div class="kpi-sub">${t('status.sickLeaveSubtitle', { percent: kpis.sickLeavePercentTotal })}</div>
              `
            : html`<div class="status-text info">${t('status.sickLeaveNoGender')}</div>`}
          <d-action plain href="${'/account/' + orgId + '/' + pageIds.staffing}"
            >${t('status.goToStaffingCalendar')}</d-action
          >
        </div>
      `;
    }
    return html``;
  }

  renderWidget(widget: Widget, interactive: boolean = true) {
    let content: TemplateResult | typeof nothing = html``;
    if (widget.name.startsWith('kpi')) {
      content = this.renderKpiWidget(widget);
    } else if (widget.name === 'myThings') {
      content = this.renderMyThings(widget);
    } else if (widget.name === 'forms') {
      // content = this.renderForms(widget);
    } else if (widget.name === 'staffing') {
      content = this.renderStaffing(widget);
    } else if (widget.name === 'overdue') {
      content = this.renderOverdue(widget);
    } else if (widget.name === 'drafts') {
      content = this.renderDrafts(widget);
    } else if (widget.name === 'issues') {
      content = this.renderIssues(widget, interactive);
    } else if (widget.name === 'updates') {
      content = this.renderUpdates(widget, interactive);
    } else if (widget.name === 'startTasks') {
      content = this.renderStartTasks(widget, interactive);
    } else if (widget.name === 'meetings') {
      content = this.renderMeetings(widget);
    } else if (widget.name === 'interviews') {
      content = this.renderInterviews(widget);
    } else if (widget.name === 'functions') {
      content = this.renderFunctions(widget);
    } else if (widget.name === 'healthcare') {
      content = this.renderHealthcare(widget);
    } else if (widget.name === 'norengros') {
      content = this.renderNorengros(widget);
    } else if (widget.name === 'risk') {
      content = this.renderRisk(widget);
    }
    return this.wrapWidgetWithEditControls(widget, content);
  }

  private wrapWidgetWithEditControls(widget: Widget, content: TemplateResult | typeof nothing) {
    if (!this.editStatusPage) {
      return content;
    }
    const isHidden = this.hiddenWidgets.includes(widget.name);
    const buttonLabel = isHidden ? t('status.show') : t('status.hide');
    const isDragging = this.draggedWidgetId === widget.name;
    const isDragOver = this.dragOverWidgetId === widget.name;
    const wrapperClass = `widget-wrapper ${isHidden ? 'widget-hidden' : ''} ${isDragging ? 'dragging' : ''} ${
      isDragOver ? 'drag-over' : ''
    }`;
    return html`
      <div
        class="${wrapperClass}"
        draggable="true"
        @dragstart=${(e: DragEvent) => this.onDragStart(e, widget.name)}
        @dragover=${(e: DragEvent) => this.onDragOver(e, widget.name)}
        @dragleave=${() => this.onDragLeave()}
        @drop=${(e: DragEvent) => this.onDrop(e, widget.name)}
        @dragend=${() => this.onDragEnd()}
      >
        <div class="drag-handle" @mousedown=${(e: MouseEvent) => e.stopPropagation()}>⋮⋮</div>
        <div class="widget-visibility-controls">
          <button @click=${() => this.onToggleWidgetVisibility(widget.name)}>${buttonLabel}</button>
        </div>
        ${content}
      </div>
    `;
  }

  renderColumn(column: Widget[]) {
    return html`<div class="column">
      ${column.map((widget: Widget) => {
        return this.renderWidget(widget);
      })}
    </div>`;
  }

  renderContent() {
    // If columns is empty but we have widgets, render them directly
    if (this.columns.length === 0 && this.widgets.length > 0) {
      return html` <div id="wrapper" class="wrapper">
          <div class="column">
            ${this.widgets.map((widget: Widget) => {
              return this.renderWidget(widget);
            })}
          </div>
        </div>
        <div id="measure">
          ${this.widgets.map((widget: Widget) => {
            return this.renderWidget(widget, false);
          })}
        </div>`;
    }

    // Normal rendering with columns
    return html` <div id="wrapper" class="wrapper">
        ${this.columns.map((column: Widget[]) => {
          return this.renderColumn(column);
        })}
      </div>
      <div id="measure">
        ${this.widgets.map((widget: Widget) => {
          return this.renderWidget(widget, false);
        })}
      </div>`;
  }

  protected willUpdate() {
    const query = location.search.slice(1);
    const params = new URLSearchParams(query);
    if (params.has('dialog') && params.has('report')) {
      const report = params.get('report') ?? '';
      history.replaceState({}, '', location.protocol + '//' + location.host + location.pathname);
      return this.onOpenFormDialog(report);
    }
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.debouncedSetColumns();
  }

  private async onOpenFormDialog(uuid: string) {
    assertIsDefined(this.pageView.currentEmployeeUuid);
    const form: FormItemForStatusPage | undefined = this.pageView.forms.find((f) => {
      return f.uuid === uuid;
    });
    if (form) {
      const result: FormDialogResult = await FormDialog.open({
        title: form.item.name,
        content: form.item.elements,
        prompt: form.prompt,
        isSurvey: form.isSurvey,
        anonymous: form.anonymous,
        userNames: this.pageView.userNames,
        currentUserUuid: this.pageView.currentEmployeeUuid,
        mode: 'editAsRespondent',
      });
      if (result.action === 'cancel') {
        return;
      }
      if (result.action === 'done') {
        const values = filterFormStructure(result.content, this.pageView.currentEmployeeUuid);
        this.dispatchEvent(
          new CustomEvent<UpdateFormValuesDetail>('update-form-values', {
            bubbles: true,
            composed: true,
            detail: {
              type: 'reports',
              uuid: uuid,
              content: values,
            },
          }),
        );
      }
    }
  }

  private async onOpenPeriodDialog(json: string) {
    const data = JSON.parse(json);
    const employee: EmployeeForStaffingCalendar = data.employee;
    const period = data.period;
    const input: EditPeriodsInput = {
      application: '',
      newPeriod: false,
      startDate: period.start,
      endDate: period.end,
      startTime: period.startTime ?? 'NONE',
      endTime: period.endTime ?? 'NONE',
      notes: period.notes,
      confirmed: period.confirmed,
      employee,
      grade: period.grade,
      type: period.type,
      days: period.days,
      currentUserHasAccess: true,
      leavePeriodEditRestriction: this.pageView.leavePeriodEditRestriction,
    };
    const result: EditPeriodsResult = await EditPeriodsDialog.open(input);

    if (result.action === 'done') {
      const value: EditPeriodResult = {
        end: result.end,
        start: result.start,
        type: result.type,
        confirmed: result.confirmed,
        notes: result.notes,
        grade: result.grade,
        employeeUuid: employee.uuid,
        newPeriod: false,
        periodId: period.uuid,
        days: result.days,
      };
      this.dispatchEvent(
        new CustomEvent<EditPeriodResult>('save-leave-period', {
          bubbles: true,
          composed: true,
          detail: value,
        }),
      );
    }
    if (result.action === 'delete') {
      this.dispatchEvent(
        new CustomEvent<{ employeeUuid: string; periodId: string }>('delete-leave-period', {
          bubbles: true,
          composed: true,
          detail: { employeeUuid: employee.uuid, periodId: period.uuid ?? '' },
        }),
      );
    }
  }

  private setColumns() {
    const width = this.getBoundingClientRect().width;
    if (width < 700) {
      this.columnsCount = 1;
    } else if (width < 1200) {
      this.columnsCount = 2;
    } else {
      this.columnsCount = 3;
    }
    const widgetHeights: NameValue[] = this.widgetHeights;

    // If no widgets, create empty columns
    if (widgetHeights.length === 0) {
      this.columns = [];
      return;
    }

    const columns: NameValue[][] = getEven(widgetHeights, this.columnsCount);
    this.columns = columns.map((column: NameValue[]) => {
      return column.map((item: NameValue) => {
        const widget: Widget | undefined = this.widgets.find((w) => {
          return w.name === item.name;
        });
        return widget || { name: '', active: false, promo: '' };
      });
    });
  }

  private onStartTaskClicked(e: CustomEvent<{ clickData: string }>) {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent<{ uuid: string }>('start-task-selected', {
        bubbles: true,
        composed: true,
        detail: { uuid: e.detail.clickData },
      }),
    );
  }

  protected renderCustomActions(): TemplateResult | typeof nothing {
    const share = !this.singleUserVersion
      ? html`<d-action class="action" @click=${() => this.onShareContent()}>${t('content.share')}</d-action>`
      : nothing;
    if (!this.featureStates.managementDashboard) {
      return share;
    }
    const moreMenuActions: ActionInput[] = [
      {
        name: this.editStatusPage ? t('status.doneEditing') : t('status.editOverview'),
        action: 'toggle-edit',
        href: '',
      },
      { name: t('status.exportExcel'), action: 'export-excel', href: '' },
    ];
    return html`
      ${share}
      <d-more-menu
        .actions=${moreMenuActions}
        @action=${(e: CustomEvent<string>) => this.onMoreMenuAction(e)}
      ></d-more-menu>
    `;
  }

  private async onMoreMenuAction(e: CustomEvent<string>) {
    if (e.detail === 'export-excel') {
      await this.onExportExcel();
    } else if (e.detail === 'toggle-edit') {
      this.onEditStatusPage();
    }
  }

  private async onExportExcel() {
    try {
      await exportStatusPageToExcel();
    } catch (error) {
      console.error('Excel-eksport feilet:', error);
    }
  }

  private loadHiddenWidgets() {
    this.hiddenWidgets = HiddenWidgetsService.getHiddenWidgets(this.pageView.organizationId);
  }

  private loadWidgetOrder() {
    this.widgetOrder = WidgetOrderService.getWidgetOrder(this.pageView.organizationId);
  }

  private onToggleWidgetVisibility(widgetName: string) {
    if (this.hiddenWidgets.includes(widgetName)) {
      HiddenWidgetsService.showWidget(this.pageView.organizationId, widgetName);
    } else {
      HiddenWidgetsService.hideWidget(this.pageView.organizationId, widgetName);
    }
    this.loadHiddenWidgets();
  }

  private onDragStart(e: DragEvent, widgetName: string) {
    this.draggedWidgetId = widgetName;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', widgetName);
    }
  }

  private onDragOver(e: DragEvent, widgetName: string) {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    this.dragOverWidgetId = widgetName;
  }

  private onDragLeave() {
    this.dragOverWidgetId = null;
  }

  private onDrop(e: DragEvent, targetWidgetName: string) {
    e.preventDefault();
    this.dragOverWidgetId = null;
    if (!this.draggedWidgetId || this.draggedWidgetId === targetWidgetName) {
      return;
    }
    const currentWidgets = this.widgets.map((w) => w.name);
    const draggedIndex = currentWidgets.indexOf(this.draggedWidgetId);
    const targetIndex = currentWidgets.indexOf(targetWidgetName);
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }
    const reordered = [...currentWidgets];
    reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, this.draggedWidgetId);
    WidgetOrderService.saveWidgetOrder(this.pageView.organizationId, reordered);
    this.loadWidgetOrder();
    this.draggedWidgetId = null;
  }

  private onDragEnd() {
    this.draggedWidgetId = null;
    this.dragOverWidgetId = null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-status-page-content': DStatusPageContent;
  }
}
